import type {
  InvitationCardBlockDto as Block,
  InvitationEditorDocumentDto as Document,
} from "@/lib/api/types";

export type InvitationTemplate = {
  envelope: { name: string; colorSlug: string; color: string; ink: string };
  id: string;
  name: string;
  category: "Floral" | "Traditional" | "Minimal" | "Celebration";
  description: string;
  fonts: string;
  paper: string;
  ink: string;
  accent: string;
  motif: "rose" | "olive" | "marigold" | "lotus" | "editorial" | "stars";
  headingFont: string;
};

export const invitationTemplates: InvitationTemplate[] = [
  {
    id: "rose-garden",
    envelope: { name: "Forest & gold", colorSlug: "color-forest", color: "#3d5345", ink: "#fff8f3" },
    name: "Rose Garden",
    category: "Floral",
    description:
      "Blush roses, soft foliage, and flowing calligraphy. A romantic garden celebration.",
    fonts: "Great Vibes + Cormorant Garamond",
    paper: "#fff8f3",
    ink: "#713b48",
    accent: "#bd8e77",
    motif: "rose",
    headingFont: '"Great Vibes", cursive',
  },
  {
    id: "olive-vows",
    envelope: { name: "Burgundy & gold", colorSlug: "color-burgundy", color: "#6b2a34", ink: "#f6f3e9" },
    name: "Olive Vows",
    category: "Floral",
    description:
      "Hand-drawn olive branches and warm ivory. Quiet, natural, and timeless.",
    fonts: "Cormorant Garamond + Montserrat",
    paper: "#f6f3e9",
    ink: "#3e5745",
    accent: "#9b9d74",
    motif: "olive",
    headingFont: '"Cormorant Garamond", Georgia, serif',
  },
  {
    id: "palace-marigold",
    envelope: { name: "Champagne & gold", colorSlug: "color-champagne", color: "#e8d9b8", ink: "#6b2a34" },
    name: "Palace Marigold",
    category: "Traditional",
    description: "A palace-inspired arch, marigold garlands, and deep festive color.",
    fonts: "Cinzel + EB Garamond",
    paper: "#742e34",
    ink: "#fff0cf",
    accent: "#e6b961",
    motif: "marigold",
    headingFont: "Cinzel, Georgia, serif",
  },
  {
    id: "lotus-courtyard",
    envelope: { name: "Blush & gold", colorSlug: "color-blush", color: "#edd5cb", ink: "#183f3d" },
    name: "Lotus Courtyard",
    category: "Traditional",
    description:
      "Lotus linework and a jade-and-gold palette, composed for a graceful celebration.",
    fonts: "Cinzel + Cormorant Garamond",
    paper: "#183f3d",
    ink: "#f4ead2",
    accent: "#d0b175",
    motif: "lotus",
    headingFont: "Cinzel, Georgia, serif",
  },
  {
    id: "ivory-editorial",
    envelope: { name: "Navy & gold", colorSlug: "color-navy", color: "#2a3a55", ink: "#f5efe5" },
    name: "Ivory Editorial",
    category: "Minimal",
    description: "Generous space, expressive serif type, and a fine terracotta rule.",
    fonts: "Playfair Display + Montserrat",
    paper: "#f5efe5",
    ink: "#302c29",
    accent: "#b47259",
    motif: "editorial",
    headingFont: '"Playfair Display", Georgia, serif',
  },
  {
    id: "midnight-celebration",
    envelope: { name: "Ivory & gold", colorSlug: "color-ivory", color: "#f4efe6", ink: "#17273f" },
    name: "Midnight Celebration",
    category: "Celebration",
    description:
      "A constellation of gold on midnight blue. Made for an evening to remember.",
    fonts: "Playfair Display + EB Garamond",
    paper: "#17273f",
    ink: "#f7edd5",
    accent: "#cfb578",
    motif: "stars",
    headingFont: '"Playfair Display", Georgia, serif',
  },
];

// Original vector ornaments. Stored as image blocks so they can be moved,
// resized, replaced, or removed with the existing editor tools.
function ornament(t: InvitationTemplate): string {
  let art = "";
  if (t.motif === "rose" || t.motif === "olive") {
    art =
      '<path d="M20 200 Q70 90 188 30 M40 165 Q130 150 198 100" fill="none" stroke="#71856a" stroke-width="2"/>';
    for (let n = 0; n < 9; n++) {
      const x = 35 + n * 17,
        y = 166 - n * 16;
      art += `<ellipse cx="${x}" cy="${y}" rx="9" ry="23" transform="rotate(${n % 2 ? 52 : -42} ${x} ${y})" fill="${n % 2 ? "#94a082" : "#667d62"}" opacity=".85"/>`;
    }
    if (t.motif === "rose")
      for (const [x, y, r] of [
        [70, 100, 26],
        [123, 63, 21],
        [145, 125, 18],
      ]) {
        art += `<g transform="translate(${x} ${y})">${Array.from({ length: 7 }, (_, n) => `<ellipse rx="${r * 0.55}" ry="${r}" transform="rotate(${n * 51}) translate(0 -7)" fill="${n % 2 ? "#e7b7ad" : "#d8a09b"}" stroke="#c58b891f"/>`).join("")}<circle r="8" fill="#bc787c"/></g>`;
      }
  } else if (t.motif === "marigold") {
    art = `<path d="M0 20 Q110 85 220 20 M0 5 Q110 55 220 5" fill="none" stroke="${t.accent}" stroke-width="2"/>`;
    for (let n = 0; n < 9; n++) {
      const x = 12 + n * 24,
        y = 35 + 25 * Math.sin((n / 8) * Math.PI);
      art += `<g transform="translate(${x} ${y})">${Array.from({ length: 8 }, (_, j) => `<ellipse rx="5" ry="12" transform="rotate(${j * 45})" fill="${n % 2 ? "#df8e35" : "#f5bf58"}"/>`).join("")}<circle r="4" fill="#9d5033"/></g>`;
    }
  } else if (t.motif === "lotus") {
    art = `<g fill="none" stroke="${t.accent}" stroke-width="2">${[-60, -30, 0, 30, 60].map((a) => `<path d="M110 172 Q65 100 110 35 Q155 100 110 172Z" transform="rotate(${a} 110 172)"/>`).join("")}<path d="M30 184 Q110 205 190 184 M50 199 H170"/></g>`;
  } else if (t.motif === "stars") {
    art = Array.from({ length: 24 }, (_, n) => {
      const x = (n * 67 + 17) % 210,
        y = (n * 43 + 11) % 205,
        r = n % 3 === 0 ? 5 : 2;
      return `<path d="M${x - r} ${y}H${x + r}M${x} ${y - r}V${y + r}" stroke="${t.accent}" stroke-width="1"/>`;
    }).join("");
  } else {
    art = `<circle cx="110" cy="110" r="85" fill="none" stroke="${t.accent}" stroke-width="1"/><path d="M25 110 H195" stroke="${t.accent}"/>`;
  }
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="220" height="220" viewBox="0 0 220 220">${art}</svg>`)}`;
}

/** A fresh editable document per use; the gallery and editor share these blocks. */
export function createTemplateDocument(t: InvitationTemplate): Document {
  let sequence = 0;
  const block = (type: string, props: Record<string, unknown>): Block => ({
    id: `${t.id}-${++sequence}`,
    type,
    props,
  });
  const rect = (
    x: number,
    y: number,
    width: number,
    height: number,
    fill: string,
    stroke?: string,
  ) =>
    block("rect", { x, y, width, height, fill, stroke, strokeWidth: stroke ? 1 : 0 });
  const bodyFont =
    t.motif === "marigold" || t.motif === "stars"
      ? '"EB Garamond", Georgia, serif'
      : t.motif === "editorial"
        ? "Montserrat, sans-serif"
        : '"Cormorant Garamond", Georgia, serif';
  const text = (
    value: string,
    y: number,
    size: number,
    options: Record<string, unknown> = {},
  ) =>
    block("text", {
      x: 65,
      y,
      width: 370,
      text: value,
      fontSize: size,
      fontFamily: bodyFont,
      fill: t.ink,
      align: "center",
      ...options,
    });
  const art = (x: number, y: number, size: number, rotation = 0) =>
    block("image", {
      x,
      y,
      width: size,
      height: size,
      url: ornament(t),
      naturalWidth: 220,
      naturalHeight: 220,
      objectFit: "contain",
      rotation,
      alt: `${t.name} ornament`,
    });
  const background = () => [
    rect(0, 0, 500, 700, t.paper),
    rect(24, 24, 452, 652, "transparent", t.accent),
    rect(31, 31, 438, 638, "transparent", t.accent),
  ];
  const floral = t.category === "Floral";
  const decorations = (): Block[] =>
    floral
      ? [art(-12, -8, 210), art(512, 708, 140, 180)]
      : t.motif === "marigold"
        ? [art(35, 35, 215), art(250, 35, 215)]
        : t.motif === "lotus"
          ? [art(190, 42, 120), art(215, 623, 70)]
          : t.motif === "stars"
            ? [art(10, 10, 210), art(490, 690, 210, 180)]
            : [];
  const front = [...background(), ...decorations()];
  if (t.motif === "marigold")
    front.push(
      block("image", {
        x: 55,
        y: 104,
        width: 390,
        height: 505,
        naturalWidth: 390,
        naturalHeight: 505,
        url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="390" height="505"><path d="M5 500V155Q5 70 110 65Q125 20 195 5Q265 20 280 65Q385 70 385 155V500" fill="none" stroke="${t.accent}" stroke-width="2"/></svg>`)}`,
        alt: "Palace arch",
      }),
    );
  front.push(
    text("TOGETHER WITH THEIR FAMILIES", 180, 12, {
      fontFamily: "Montserrat, sans-serif",
    }),
    text("Archi", 238, t.motif === "rose" ? 70 : 55, { fontFamily: t.headingFont }),
    text("&", 320, 30, { italic: true, fill: t.accent }),
    text("Dhruv", 365, t.motif === "rose" ? 70 : 55, { fontFamily: t.headingFont }),
    text("invite you to celebrate their wedding", 456, 18),
    rect(213, 503, 74, 1, t.accent),
    text("14 FEBRUARY 2027  ·  5:00 PM", 530, 13, {
      fontFamily: "Montserrat, sans-serif",
    }),
    text("Your venue · Your city", 566, 18),
    text("An evening of love, laughter & togetherness", 603, 12, { italic: true }),
  );
  const back = [
    ...background(),
    ...decorations(),
    text("The celebration", 190, 34, { fontFamily: t.headingFont }),
    rect(213, 251, 74, 1, t.accent),
    text("THE CEREMONY", 285, 13, { fontFamily: "Montserrat, sans-serif" }),
    text("5:00 PM · Your ceremony venue", 319, 20),
    text("DINNER & CELEBRATION", 386, 13, { fontFamily: "Montserrat, sans-serif" }),
    text("7:00 PM · Your reception venue", 420, 20),
    text("Kindly respond by your RSVP date", 493, 17, { italic: true }),
    text("Replace these sample details with your own.", 539, 12),
  ];
  return {
    version: 1,
    schemaVersion: "1",
    mainCard: { id: `${t.id}-front`, variant: t.id, blocks: front },
    backCard: { id: `${t.id}-back`, variant: t.id, blocks: back },
    envelope: {},
  };
}
